import { useCallback, useState } from "react";
import type { RemoteDevice } from "@/mocks/device";

const STORAGE_KEY = "aura-device-history";
const MAX_HISTORY = 5;

function loadHistory(): RemoteDevice[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as RemoteDevice[]) : [];
  } catch {
    return [];
  }
}

function persist(history: RemoteDevice[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch {
    // ignore quota / unavailable storage errors
  }
}

export default function useDeviceHistory() {
  const [history, setHistory] = useState<RemoteDevice[]>(() => loadHistory());

  const saveDevice = useCallback((device: RemoteDevice) => {
    setHistory((prev) => {
      const next = [device, ...prev.filter((d) => d.code !== device.code)].slice(
        0,
        MAX_HISTORY,
      );
      persist(next);
      return next;
    });
  }, []);

  const removeDevice = useCallback((code: string) => {
    setHistory((prev) => {
      const next = prev.filter((d) => d.code !== code);
      persist(next);
      return next;
    });
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  return { history, saveDevice, removeDevice, clearHistory };
}